import { useState } from "react";
import { roleLabel } from "../lib/format.js";

export const themeOptions = [
  { id: "sage", label: "Sage", swatch: ["#0a7f70", "#c88a4d"] },
  { id: "aurora", label: "Aurora", swatch: ["#236fa1", "#8a5bc7"] },
  { id: "sunrise", label: "Sunrise", swatch: ["#b8613f", "#9f5470"] },
  { id: "midnight", label: "Midnight", swatch: ["#47b7a6", "#7fa7d7"] },
];

export function LoginScreen({ users, onLogin, error, workspace, isTrial = false }) {
  const safeUsers = Array.isArray(users) ? users : [];
  const [userId, setUserId] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [forgotPassword, setForgotPassword] = useState(false);
  const selectedUser = userId || safeUsers[0]?.id || "";
  const companyName = workspace?.companyName || "Demo Workspace";
  const loginMode = workspace?.loginMode || "demo";
  const passwordLogin = loginMode === "password";
  const ldapLogin = loginMode === "ldap";
  const secureLogin = passwordLogin || ldapLogin;
  const loginTitle = secureLogin ? `${companyName} secure sign-in` : isTrial ? `${companyName} trial workspace` : "Open demo workspace";
  const loginCopy = passwordLogin
    ? "Sign in with the username or email and password provided by your CRM administrator."
    : ldapLogin
    ? "This customer workspace uses enterprise authentication. Contact your administrator if your organization sign-in is not available."
    : isTrial
    ? "Choose a trial user for this customer workspace. Data is isolated to this customer."
    : "Choose a demo role. The backend returns a scoped JWT bearer token and user profile.";

  return (
    <section className="login-shell">
      <div className="login-visual">
        <div className="brand-mark">{workspace?.branding?.shortName || "FS"}</div>
        <div>
          <h1>{isTrial ? `${companyName} field sales trial.` : "Field sales CRM for disciplined execution."}</h1>
          <p>{isTrial ? "A private trial workspace for prospects, visits, GPS check-ins, product flows, and manager analytics." : "React front end, REST backend, structured prospecting, calls, visits, GPS check-ins, and manager analytics."}</p>
        </div>
      </div>
      <div className="login-panel">
        <form
          className="login-card"
          onSubmit={(event) => {
            event.preventDefault();
            if (passwordLogin) {
              onLogin({ username, password });
              return;
            }
            onLogin(selectedUser);
          }}
        >
          <h2>{loginTitle}</h2>
          <p className="subtle">{loginCopy}</p>
          {isTrial ? <div className="trial-badge">Trial workspace</div> : null}
          {error ? <div className="notice">{error}</div> : null}
          {ldapLogin ? (
            <div className="notice">Enterprise sign-in must be configured by the workspace administrator before users can enter this CRM.</div>
          ) : passwordLogin ? (
            <>
              <div className="field">
                <label htmlFor="loginUsername">Username or email</label>
                <input
                  id="loginUsername"
                  type="text"
                  value={username}
                  autoComplete="username"
                  required
                  maxLength={180}
                  onChange={(event) => setUsername(event.target.value)}
                />
              </div>
              <div className="field">
                <label htmlFor="loginPassword">Password</label>
                <input
                  id="loginPassword"
                  type="password"
                  value={password}
                  autoComplete="current-password"
                  required
                  maxLength={128}
                  onChange={(event) => setPassword(event.target.value)}
                />
              </div>
              <button className="ghost-btn" type="button" onClick={() => setForgotPassword(!forgotPassword)} aria-expanded={forgotPassword}>Forgot password?</button>
              {forgotPassword ? <div className="notice" role="status">
                Contact your workspace administrator and share your username and workspace link. Only an administrator can reset another user's password. If your administrator is locked out, contact the platform operator. They will provide a temporary password after confirming your identity; you must change it when you sign in. No email or SMS is sent from this page.
              </div> : null}
              <button className="primary-btn" type="submit" disabled={!username.trim() || !password}>
                Enter CRM
              </button>
            </>
          ) : (
            <>
              <div className="field">
                <label htmlFor="userSelect">User</label>
                <select id="userSelect" value={selectedUser} onChange={(event) => setUserId(event.target.value)}>
                  {safeUsers.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name} - {roleLabel(user.role)}
                    </option>
                  ))}
                </select>
              </div>
              <button className="primary-btn" type="submit" disabled={!selectedUser}>
                Enter CRM
              </button>
            </>
          )}
        </form>
      </div>
    </section>
  );
}

export function ChangePasswordScreen({ user, workspace, error, sessionWarning, onChangePassword, onLogout }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState("");
  const companyName = workspace?.companyName || "CRM Workspace";
  const blocked = submitting || !currentPassword || !newPassword || !confirmPassword;

  async function submitPasswordChange(event) {
    event.preventDefault();
    setLocalError("");
    if (newPassword !== confirmPassword) {
      setLocalError("New password and confirmation do not match.");
      return;
    }
    setSubmitting(true);
    try {
      const changed = await onChangePassword({ currentPassword, newPassword, confirmPassword });
      if (changed) {
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="login-shell">
      <div className="login-visual">
        <div className="brand-mark">{workspace?.branding?.shortName || "FS"}</div>
        <div>
          <h1>{companyName} password setup.</h1>
          <p>Set your own password before opening customer, team, and field activity data.</p>
        </div>
      </div>
      <div className="login-panel">
        <form className="login-card" onSubmit={submitPasswordChange}>
          <h2>Change password on first login</h2>
          <p className="subtle">
            {user?.name ? `${user.name}, your current password is temporary. ` : ""}
            Choose a private password that only you know.
          </p>
          {sessionWarning ? <div className="notice">{sessionWarning}</div> : null}
          {localError || error ? <div className="notice">{localError || error}</div> : null}
          <div className="field">
            <label htmlFor="currentPassword">Current temporary password</label>
            <input
              id="currentPassword"
              type="password"
              value={currentPassword}
              autoComplete="current-password"
              required
              maxLength={128}
              onChange={(event) => setCurrentPassword(event.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="newPassword">New password</label>
            <input
              id="newPassword"
              type="password"
              value={newPassword}
              autoComplete="new-password"
              required
              minLength={10}
              maxLength={128}
              onChange={(event) => setNewPassword(event.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="confirmPassword">Confirm new password</label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              autoComplete="new-password"
              required
              minLength={10}
              maxLength={128}
              onChange={(event) => setConfirmPassword(event.target.value)}
            />
          </div>
          <button className="primary-btn" type="submit" disabled={blocked}>
            {submitting ? "Changing..." : "Set New Password"}
          </button>
          <button className="ghost-btn full" type="button" onClick={onLogout}>
            Sign Out
          </button>
        </form>
      </div>
    </section>
  );
}

export function Sidebar({ user, view, setView, onLogout, theme, setTheme, isOpen = true, onClose }) {
  const items = [
    ["dashboard", "Dashboard"],
    ["teams", "Team Performance"],
    ["customers", "Prospects"],
    ["activities", "Calls & Visits"],
    ["field", "Field Tracking"],
    ["lifecycle", "Lifecycle"],
    ["products", "Products & Orders"],
    ["operations", "Targets & Costs"],
    ["engagement", "Support & Messaging"],
    ["incentives", "Incentives"],
    ["reports", "Reports"],
  ];
  if (user.role === "admin") items.push(["admin", "Admin"]);

  function openView(id) {
    setView(id);
    if (typeof window === "undefined" || !window.matchMedia("(max-width: 980px)").matches) return;
    onClose?.();
    window.requestAnimationFrame(() => {
      document.querySelector(".main")?.scrollIntoView({ block: "start" });
    });
  }

  return (
    <aside className="sidebar" aria-hidden={!isOpen}>
      <div className="sidebar-header">
        <div className="brand-mark">FS</div>
        <div className="sidebar-title">
          Field CRM
          <br />
          <span>Phase 1 MVP</span>
        </div>
        <button className="drawer-toggle drawer-toggle-inside active" type="button" aria-label="Close navigation menu" onClick={onClose}>
          <span />
          <span />
          <span />
        </button>
      </div>
      <label className="mobile-view-switcher">
        <span>Current section</span>
        <select aria-label="Current CRM section" value={view} onChange={(event) => openView(event.target.value)}>
          {items.map(([id, label]) => (
            <option key={id} value={id}>
              {label}
            </option>
          ))}
        </select>
      </label>
      <nav className="nav">
        {items.map(([id, label]) => (
          <button key={id} className={view === id ? "active" : ""} onClick={() => openView(id)}>
            {label}
          </button>
        ))}
      </nav>
      <div className="sidebar-footer">
        <ThemePicker theme={theme} setTheme={setTheme} />
        <div className="user-chip">
          <strong>{user.name}</strong>
          <span>
            {user.designation || roleLabel(user.role)} · {user.territory}
          </span>
        </div>
        <button className="ghost-btn" onClick={onLogout}>
          Logout
        </button>
      </div>
    </aside>
  );
}

function ThemePicker({ theme, setTheme }) {
  return (
    <div className="theme-picker" aria-label="Theme picker">
      <span>Theme</span>
      <div className="theme-options">
        {themeOptions.map((option) => (
          <button
            key={option.id}
            className={theme === option.id ? "active" : ""}
            type="button"
            aria-pressed={theme === option.id}
            onClick={() => setTheme(option.id)}
          >
            <span
              className="theme-swatch"
              style={{ "--swatch-a": option.swatch[0], "--swatch-b": option.swatch[1] }}
            />
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}
